/**
 * iter86.41 — Season Pot Pick'em panel (single league, Phase 1).
 *
 * The golf Lowest-Average pot picker adapted for club seasons: pick 5
 * clubs — 1 title contender (Pot 1 · ranks 1-4), 2 chasers (Pot 2 ·
 * 5-10), 2 longshots (Pot 3 · 11+). Overlap across crewmates is
 * allowed — this is NOT a draft. W/D/L points all season (3/1/0);
 * the Power Pick multiplies ONE club's points by its pot (1x/1.5x/2x).
 * Picks lock at the admin lock or first kickoff, whichever comes first.
 *
 * iter86.42 — Phase 2 (Super Draft Pick'em): same panel drives the
 * composite PL+La Liga+Serie A pool (pick 2/2/3 = 7) plus the Champions
 * Pick — nominate ANY club in the pool to top the combined table for a
 * +15 bonus at season completion (stored via the knockout_pick fields,
 * same convention as golf).
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { myPicksForTournament } from "@/lib/tournamentFormat";
import { CheckCircle2, ChevronRight, Lock, AlertTriangle, Info } from "lucide-react";
import { Baseball, Basketball } from "@phosphor-icons/react";
import { toast } from "sonner";
import { nationalFlag } from "@/lib/flags";
import PickSlip from "@/components/onui/PickSlip";
import { isTournamentComplete, TOURNAMENT_COMPLETE_LOCK_COPY } from "@/lib/todoStatus";
import { isFootballPlayoffsTemplate } from "@/lib/footballPlayoffsTemplates";
import { isPlayoffFieldFinal } from "@/lib/playoffField";

const COUNT_WORDS = { 5: "Five", 6: "Six", 7: "Seven", 8: "Eight" };

export default function SeasonPotPickPanel({ tournament, me, isLeagueAdmin, onPicked, hideCollapse = false, footerSlot = null }) {
    const [state, setState] = useState(null);
    const [selected, setSelected] = useState([]);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(null); // null = derive from saved state

    // iter86.77.18 — per-tournament picks (flat field leaks the ACTIVE
    // tournament's ids on multi-tournament leagues → "Unknown club").
    const myPicks = useMemo(() => myPicksForTournament(me, tournament),
        [me?.assigned_team_ids_by_tournament, me?.assigned_team_ids, tournament?.id, tournament?.teams]); // eslint-disable-line react-hooks/exhaustive-deps
    const myPicksKey = useMemo(() => JSON.stringify([...myPicks].sort()), [myPicks]);
    // iter87.261 (operator-requested) — a picked team can slide out of
    // the playoff field after the pick was made (bubble team, race
    // still fluid at pick time). Once dropped, `mlb_pot_for_team`/
    // `nba_pot_for_team` exclude it from `state.pots[].teams` entirely,
    // so it silently vanishes from the grid below with no way to see
    // WHICH club it was — look it up from the full tournament roster
    // (never pot-filtered) instead.
    const teamsById = useMemo(
        () => Object.fromEntries((tournament?.teams || []).map(t => [t.id, t])),
        [tournament?.teams]
    );

    useEffect(() => {
        if (!tournament?.id) return;
        api.getSeasonPickemState(tournament.id).then(setState).catch(() => {});
    }, [tournament?.id, tournament?.pickem?.picks_locked_by_admin,
        tournament?.round_started?.group]);

    // Sync from server only when pick CONTENT changes (mount, post-save).
    // iter86.68 — Guard: never clobber an on-screen selection with a
    // TRANSIENT empty payload (redacted/partial fetch) — that flashed the
    // board clear once mid-flow (user-reported).
    useEffect(() => {
        if (myPicks.length || selected.length === 0) setSelected(myPicks);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myPicksKey]);

    if (!state || state.mode !== "pot" || !me) return null;

    const locked = state.picks_locked;
    const required = state.picks_required || 5;
    const hasAllPicks = myPicks.length === required;
    if (locked && myPicks.length === 0) {
        // iter87.43 — Unify locked-state copy when the tournament is
        // done. During the season we say "closed at kickoff"; once the
        // tournament ends, that wording is confusing (kickoff was weeks
        // ago). Defer to the shared TOURNAMENT_COMPLETE_LOCK_COPY.
        const complete = isTournamentComplete(tournament);
        return (
            <div className="brutal-card p-4 text-xs text-zinc-500" data-testid="season-pickem-closed-note">
                <Lock className="w-3.5 h-3.5 inline mr-1" />
                {complete ? TOURNAMENT_COMPLETE_LOCK_COPY : "Club picks closed at kickoff."}
            </div>
        );
    }

    const countIn = (pot, list = selected) => list.filter(id =>
        (pot.teams || []).some(t => t.id === id)).length;

    // iter87.282 (user-requested) — clicking a NEW team now auto-drops
    // any invalid/dropped pick still lingering in the slip instead of
    // forcing a manual "x" deselect first: a dropped team's live seed
    // already excluded it from every pot's grid, so keeping it
    // selected only ever inflates the pick count for no reason.
    const toggle = (pot, teamId) => {
        if (locked || potPreviewMode) return;
        if (selected.includes(teamId)) {
            setSelected(selected.filter(id => id !== teamId));
            return;
        }
        const cleaned = selected.filter(id => !(teamsById[id]?.missed_playoffs));
        if (countIn(pot, cleaned) >= pot.picks) {
            toast.error(`Pot ${pot.pot} allows ${pot.picks} pick${pot.picks > 1 ? "s" : ""} — deselect one first`);
            return;
        }
        setSelected([...cleaned, teamId]);
    };

    // iter87.282 — post-lock, the ONLY tile interaction allowed is the
    // dropped-pick swap flow. Tapping ANY other team in the same pot
    // as a dropped pick now swaps it in immediately — no need to tap
    // the dropped tile first (was a 2-step arm-then-swap flow).
    const onTileClick = (pot, t) => {
        if (potPreviewMode) return;
        if (locked) {
            if (t.missed_playoffs || selected.includes(t.id)) return;
            const droppedInPot = (pot.teams || []).find(x => selected.includes(x.id) && x.missed_playoffs);
            if (droppedInPot) replaceDrop(droppedInPot.id, t.id);
            return;
        }
        toggle(pot, t.id);
    };

    const submit = async () => {
        setSaving(true);
        try {
            const res = await api.submitSeasonPickemPicks(tournament.id, me.id, selected);
            toast.success("Club picks locked in!");
            if (res?.power_pick_cleared) {
                toast.info("Your Power Pick was cleared — the club you dropped was your Power Pick. Choose a new one from the Power Up card.");
            }
            setOpen(false);
            onPicked?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Picks failed");
        }
        setSaving(false);
    };

    // iter87.278.2 (user-requested — "the player has to get notified
    // that the drafted team drops out ... in addition to turning the
    // tile back to live since the team is no longer an option") — the
    // ONE thing a player can still change after lock: swapping a
    // dropped pick for a same-pot replacement. `replaceDrop` calls the
    // backend carve-out endpoint; the tile grid above lets the player
    // pick the new team directly (iter87.282 — single click, no arm/cancel step).
    const replaceDrop = async (oldId, newId) => {
        try {
            const res = await api.replaceDroppedPotPick(tournament.id, me.id, oldId, newId);
            setSelected(res.picks || selected.map((id) => (id === oldId ? newId : id)));
            toast.success("Swapped in — that pick is live again.");
            onPicked?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't swap that pick");
        }
    };

    // iter87.6 — Admin lock/unlock moved to Tournament Admin → Danger Zone.

    const total = selected.length;
    const complete = state.pots.every(p => countIn(p) === p.picks);
    const dirty = JSON.stringify([...selected].sort()) !== myPicksKey;
    const isOpen = hideCollapse ? true : (open === null ? !(hasAllPicks || locked) : open);
    const mults = state.power_pick_multipliers || {};
    const isNfl = state.template === "nfl_season_api";
    const isMlb = state.template === "mlb_playoffs_api";
    // iter87.225 — NBA Playoffs, sibling of MLB above.
    const isNba = state.template === "nba_playoffs_api";
    // iter87.230 — NFL/CFB Playoffs: round-based scoring (6→8→10→12),
    // no per-game or W/D/L copy applies here.
    const isFootballPO = isFootballPlayoffsTemplate(state.template);
    const multsText = Object.keys(mults).length
        ? Object.keys(mults).sort((a, b) => Number(a) - Number(b)).map((k) => `${Number(mults[k])}x`).join(" / ")
        : "1x / 1.5x / 2x";
    const countWord = COUNT_WORDS[required] || required;
    const droppedSelected = selected
        .map(id => teamsById[id])
        .filter(t => t && t.missed_playoffs);
    const fieldFinal = isPlayoffFieldFinal(tournament);
    // iter87.496 (user-reported — "EPL 2H ... should freeze and be
    // based on the current table" + "once match day 19 is finished
    // ... this should unlock for picking") — EPL/La Liga 2nd-half
    // tournaments preview the live current table but stay locked for
    // actual picking until Matchday 19 finishes (`pot_table_final`
    // flips to true server-side the moment that happens).
    const potPreviewMode = state.pot_table_final === false;

    // iter87.34 — Champions Pick moved to its own To-Do tile
    // (see `TodoChips.jsx` → "Champions Pick" → `KnockoutPickModal`).
    // This panel is now pot-picks-only. No local champion state,
    // no `saveChampionsPick`, no `onPicked` race.

    return (
        <div className={hideCollapse ? "" : "brutal-card p-0"} data-testid="season-pickem-panel" data-open={isOpen}>
            {!hideCollapse && (
            <button
                type="button"
                onClick={() => setOpen(!isOpen)}
                data-testid="season-pickem-toggle-btn"
                className="w-full p-4 flex items-center justify-between gap-2 flex-wrap rounded-t-2xl hover:bg-zinc-900/50 transition-colors"
            >
                <h3 className="font-display font-extrabold text-lg uppercase tracking-tight text-left flex items-center gap-1.5">
                    {isMlb ? <Baseball size={18} weight="duotone" className="shrink-0" /> : isNba ? <Basketball size={18} weight="duotone" className="shrink-0" /> : (isFootballPO || (state.league_label || "").toUpperCase().includes("NFL")) ? "🏈" : "⚽"}
                    Your {countWord} — {state.league_label}
                </h3>
                <span className="flex items-center gap-2 shrink-0">
                    {locked ? (
                        <span className="pill bg-heat/20 border-heat text-heat text-[9px]" data-testid="season-pickem-locked-badge">
                            <Lock className="w-2.5 h-2.5" /> LOCKED
                        </span>
                    ) : hasAllPicks && !dirty ? (
                        <span className="pill bg-neon/15 border-neon text-neon text-[9px]" data-testid="season-pickem-saved-badge">
                            <CheckCircle2 className="w-2.5 h-2.5" /> PICKS IN
                        </span>
                    ) : (
                        <span className="pill bg-ink border-[#333] text-zinc-400 text-[10px]" data-testid="season-pickem-count">
                            {total}/{required} picked
                        </span>
                    )}
                    <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </span>
            </button>
            )}

            {isOpen && (
            <div className={`space-y-4 ${hideCollapse ? "p-0" : "p-5 pt-0"}`} data-testid="season-pickem-panel-body">
            {(isMlb || isNba) && !fieldFinal && (
                <div
                    data-testid="season-pickem-seeding-not-final-banner"
                    className="text-volt text-[11px] font-semibold flex items-start gap-1.5 bg-volt/10 border border-volt/40 rounded-lg px-3 py-2"
                >
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                        Seeding isn't final yet — the pots below are projected only and can still shift before the
                        last day of the regular season. Come back to make your final picks once the season ends
                        and seeding is official.
                    </span>
                </div>
            )}
            {potPreviewMode && (
                <div
                    data-testid="season-pickem-pot-preview-banner"
                    className="text-volt text-[11px] font-semibold flex items-start gap-1.5 bg-volt/10 border border-volt/40 rounded-lg px-3 py-2"
                >
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                        Showing this season's current table — pots are a live preview and locked for picking.
                        Final pots lock the moment Matchday 19 finishes, and picking opens automatically.
                    </span>
                </div>
            )}
            <div className="text-[11px] text-zinc-500 flex gap-2 items-start text-left">
                <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                    <span className="text-white font-bold uppercase tracking-tight">How it works:</span>{" "}
                    Pick <b className="text-cyber">{state.pots.map(p => `${p.picks} from Pot ${p.pot}`).join(", ")}</b>.
                    Crewmates CAN pick the same {(isMlb || isNba || isFootballPO) ? "teams" : isNfl ? "teams" : "clubs"}.
                    {(isMlb || isNba) ? (
                        <> Every individual game your rostered teams play scores <b className="text-cyber">+6 for a win · −2 for a loss</b> — most points wins.</>
                    ) : isFootballPO ? (
                        <> Every playoff win scores more as the round gets deeper — <b className="text-cyber">6 → 8 → 10 → 12 points</b> — most points wins.</>
                    ) : (
                        <> Every {isNfl ? "game your teams" : "league match your clubs"} play scores
                        <b className="text-cyber"> 3 for a win · 1 for a {isNfl ? "tie" : "draw"} · 0 for a loss</b> — most points wins.</>
                    )}
                    {" "}Your Power Pick multiplies one {(isMlb || isNba || isFootballPO) ? "team" : isNfl ? "team" : "club"} by its pot ({multsText}). Everything locks at first {isMlb ? "pitch" : isNba ? "tip-off" : "kickoff"}.
                </p>
            </div>
            {state.pot_basis && (
                <p className="text-[10px] text-zinc-600 italic" data-testid="season-pickem-pot-basis">
                    ℹ️ {state.pot_basis}
                </p>
            )}
            {droppedSelected.length > 0 && (
                <div
                    data-testid="season-pickem-dropped-notice"
                    className="text-amber-400 text-[11px] font-semibold flex items-start gap-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2"
                >
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                        {droppedSelected.map(t => t.name).join(", ")}
                        {fieldFinal
                            ? ` missed the playoff field. ${locked ? "Tap another team in the same pot below to swap it in — it's live again." : "Just tap a new team in that pot below — it'll swap in automatically."}`
                            : ` ${droppedSelected.length > 1 ? "are" : "is"} currently outside the projected field — seeding isn't final yet, this can still change before the last day of the regular season.`}
                    </span>
                </div>
            )}

            {state.pots.map(pot => (
                <div key={pot.pot} data-testid={`season-pot-${pot.pot}`}>
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                            Pot {pot.pot} · {pot.label} · pick {pot.picks} · Power Pick {isFootballPO && mults[String(pot.pot)] === undefined ? "Not eligible" : `${Number(mults[String(pot.pot)] ?? 1)}x`}
                        </p>
                        <span className={`text-[10px] font-mono ${countIn(pot) === pot.picks ? "text-neon" : "text-zinc-500"}`}>
                            {countIn(pot)}/{pot.picks}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                        {(pot.teams || []).map(t => {
                            const on = selected.includes(t.id);
                            // iter87.282 — dropped pick (amber, no longer
                            // interactive — swapping now happens by
                            // tapping the REPLACEMENT directly) vs. a
                            // "live again" swap candidate (cyber outline,
                            // clickable whenever THIS pot has a dropped
                            // pick) vs. the normal locked/pickable states.
                            const isDropped = locked && t.missed_playoffs && on;
                            const isSwapCandidate = locked && !isDropped && !on
                                && (pot.teams || []).some(x => selected.includes(x.id) && x.missed_playoffs);
                            const tileDisabled = (locked && !isSwapCandidate) || potPreviewMode;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    data-testid={`season-pick-${t.id}`}
                                    onClick={() => onTileClick(pot, t)}
                                    disabled={tileDisabled}
                                    className={`relative p-3 min-h-[56px] border rounded-lg text-left transition-all ${
                                        tileDisabled ? "cursor-default opacity-70" : "cursor-pointer active:scale-[0.98]"
                                    } ${
                                        isDropped ? "border-amber-500 bg-amber-500/15 animate-pulse"
                                            : isSwapCandidate ? "border-cyber bg-cyber/15 ring-1 ring-cyber/50"
                                            : on ? "border-cyber bg-cyber/10" : "border-white/[0.08] bg-[#1A1A1A] hover:border-white/25"
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-[9px] font-mono text-zinc-500">Tier {t.seed || "–"}</span>
                                        {isDropped ? (
                                            <span className="text-[8px] font-bold text-amber-400" data-testid={`season-pick-dropped-badge-${t.id}`}>
                                                DROPPED
                                            </span>
                                        ) : on ? (
                                            <CheckCircle2 className="w-3.5 h-3.5 text-cyber" />
                                        ) : isSwapCandidate ? (
                                            <span className="text-[8px] font-bold text-cyber">TAP TO SWAP IN</span>
                                        ) : null}
                                    </div>
                                    <div className="text-xs font-bold leading-tight mt-0.5 truncate">
                                        {nationalFlag(t.flag) && <span className="mr-1">{nationalFlag(t.flag)}</span>}{t.name}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}

            {/* iter87.34 — Champions Pick extracted to its own To-Do tile
                (see `TodoChips.jsx` → "Champions Pick" chip → opens
                `KnockoutPickModal`). Structurally mirrors WC KO /
                Tournament templates. This panel now handles ONLY the
                pot-picks flow; Champions Pick lives on its own so it
                can't reset the modal mid-flow (iter87.32) or clutter
                the season-picks decision. */}

            {/* iter87.6 — Lock crew picks moved to Tournament Admin → Danger Zone. */}
            {/* iter87.0 — Slip is sticky INSIDE the panel: rides the screen
                bottom while picking, docks at the panel's last row.
                iter87.406 (user-requested) — when embedded in a modal
                that hands over its floating footer slot (PotPicksModal),
                render flush against the modal's bottom border instead. */}
            <PickSlip
                visible={!locked && !potPreviewMode}
                count={total}
                required={required}
                label={(isNfl || isFootballPO) ? "teams picked" : "clubs picked"}
                chips={selected.map((id) => {
                    const t = state.pots.flatMap((p) => p.teams || []).find((x) => x.id === id) || teamsById[id];
                    return { id, label: t?.name || "Club" };
                })}
                onRemoveChip={(id) => setSelected((prev) => prev.filter((x) => x !== id))}
                submitLabel={saving ? "Saving…" : hasAllPicks ? (dirty ? "Update" : "Saved ✓") : "Lock In"}
                onSubmit={submit}
                submitDisabled={!complete || saving || !dirty}
                submitTestId="season-pickem-submit-btn"
                testId="season-pick-slip"
                portalTarget={footerSlot}
            />
            </div>
            )}
        </div>
    );
}
